/**
 * WebABTestsPage
 *
 * Displays all Web A/B tests in a project with CRUD operations and folder support.
 *
 * Features:
 * - Table view of A/B tests (Name, Status, Variants, URL, Updated, Actions)
 * - Folder navigation for Web A/B tests
 * - Create new A/B test via WebABTestDialog
 * - Create/Move/Delete folders
 * - Edit existing A/B test
 * - Publish A/B test via DeployWebPlacementDialog
 * - Delete A/B test with confirmation
 *
 * Pattern: Web Publishing A/B test management
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  AlertTitle,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Breadcrumbs,
  Link,
  Divider,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Publish as PublishIcon,
  Delete as DeleteIcon,
  CreateNewFolder as CreateNewFolderIcon,
  Folder as FolderIcon,
  DriveFileMove as MoveIcon,
  Science as ScienceIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { useAuth } from '@frontend/contexts/AuthContext';
import { WebPlacementService } from '@modules/firebase-core/services/WebPlacementService';
import { WebPlacementDeployService } from '@modules/webplacements/services/WebPlacementDeployService';
import { WebABTestDialog } from '../components/WebABTestDialog';
import { DeployWebPlacementDialog } from '../components/DeployWebPlacementDialog';
import { CreateWebABTestFolderDialog } from '../components/CreateWebABTestFolderDialog';
import { MoveWebABTestToFolderDialog } from '../components/MoveWebABTestToFolderDialog';
import { SDialog } from '@modules/ui-components';
import { useWebABTestFolders } from '../hooks/useWebABTestFolders';
import { useWebPlacementDeploy } from '../hooks/useWebPlacementDeploy';
import { useCustomDomain } from '@modules/custom-domains';
import type {
  WebABIterationPublishInput,
  WebPlacement,
  CreateWebPlacementInput,
  UpdateWebPlacementInput,
} from '../types/webplacements.types';
import { getFunnelUrl } from '../types/webplacements.types';
import type { WebABTestFolder } from '@modules/firebase-core/converters/webABTestFolderConverter';

/** Header row height = the 44px header buttons, in both themes. */
const AB_TESTS_HEADER_ROW_MIN_HEIGHT_PX = 44;
/**
 * Title row bottom margin (theme spacing, 30px): the row overhangs the 32px
 * title by 6–7px, so the content starts ~37px under the title — the same
 * offset as the Onboardings table under its reserved bulk slot (D-31).
 */
const AB_TESTS_HEADER_ROW_MB = 3.75;

/**
 * Union type for list items (folder or A/B test)
 */
type WebABTestListItem =
  | (WebABTestFolder & { itemType: 'folder' })
  | (WebPlacement & { itemType: 'abtest' });

/**
 * Format date for display
 */
const formatDate = (isoDate: string): string => {
  try {
    const date = new Date(isoDate);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return isoDate;
  }
};

/**
 * Format variants for display
 */
const formatVariants = (variants: WebPlacement['variants']): string => {
  if (!variants || variants.length === 0) return '—';
  const count = variants.length;
  const weights = variants.map((v) => `${v.weight}%`).join('/');
  return `${count} variant${count !== 1 ? 's' : ''} (${weights})`;
};

/**
 * WebABTestsPage Component
 */
export function WebABTestsPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const { currentUser, isAdmin } = useAuth();

  // State
  const [abTests, setAbTests] = useState<WebPlacement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);

  // Folder navigation state
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [currentFolderName, setCurrentFolderName] = useState<string | null>(null);

  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createFolderDialogOpen, setCreateFolderDialogOpen] = useState(false);
  const [editingAbTest, setEditingAbTest] = useState<WebPlacement | null>(null);
  const [publishingAbTest, setPublishingAbTest] = useState<WebPlacement | null>(null);
  const [deletingItem, setDeletingItem] = useState<WebABTestListItem | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [folderContentsCount, setFolderContentsCount] = useState(0);

  // Move dialog state
  const [movingAbTest, setMovingAbTest] = useState<WebPlacement | null>(null);

  // Menu state
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuItem, setMenuItem] = useState<WebABTestListItem | null>(null);

  // Services
  const webPlacementServiceRef = useRef(new WebPlacementService());
  const deployServiceRef = useRef<WebPlacementDeployService | null>(null);
  const getDeployService = () => {
    if (!deployServiceRef.current) {
      deployServiceRef.current = new WebPlacementDeployService(webPlacementServiceRef.current);
    }
    return deployServiceRef.current;
  };

  // Hooks
  const { deploy } = useWebPlacementDeploy();

  // Custom domain for URL generation
  const { domain: customDomain } = useCustomDomain(projectId || '');
  const activeCustomDomain = customDomain?.status === 'active' ? customDomain.domain : null;

  // Folder hook
  const {
    folders,
    loading: foldersLoading,
    createFolder,
    deleteFolder,
    getFolderContentsCount,
    refresh: refreshFolders,
  } = useWebABTestFolders({ projectId: projectId || '' });

  // Load data based on current folder
  const loadData = useCallback(async () => {
    if (!projectId) {
      setError('Project ID is required');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const service = webPlacementServiceRef.current;
      const testsData = currentFolderId
        ? await service.listABTestsByFolder(projectId, currentFolderId)
        : await service.listRootABTests(projectId);
      setAbTests(testsData);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load data';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [projectId, currentFolderId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Combined items: folders first (only at root level), then A/B tests
  const items = useMemo((): WebABTestListItem[] => {
    const folderItems: WebABTestListItem[] = currentFolderId === null
      ? folders.map((folder) => ({ ...folder, itemType: 'folder' as const }))
      : [];
    const abTestItems: WebABTestListItem[] = abTests.map((abTest) => ({
      ...abTest,
      itemType: 'abtest' as const,
    }));
    return [...folderItems, ...abTestItems];
  }, [folders, abTests, currentFolderId]);

  // Folder navigation handlers
  const handleFolderClick = useCallback((folder: WebABTestFolder) => {
    setCurrentFolderId(folder.id);
    setCurrentFolderName(folder.name);
  }, []);

  const handleNavigateToRoot = useCallback(() => {
    setCurrentFolderId(null);
    setCurrentFolderName(null);
  }, []);

  // Menu handlers
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, item: WebABTestListItem) => {
    event.stopPropagation();
    setMenuAnchor(event.currentTarget);
    setMenuItem(item);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuItem(null);
  };

  // Copy URL handler
  const handleCopyUrl = useCallback(async (url: string, event: React.MouseEvent) => {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(url);
      setTimeout(() => setCopiedUrl(null), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  }, []);

  // CRUD handlers
  const handleCreateFolder = async (name: string) => {
    await createFolder(name);
    await refreshFolders();
  };

  const handleDelete = async () => {
    if (!projectId || !deletingItem) return;
    setDeleteLoading(true);
    try {
      if (deletingItem.itemType === 'folder') {
        await deleteFolder(deletingItem.id);
        await refreshFolders();
      } else {
        // Deletes the GCS routing artifact too (best-effort) so the alias can
        // be reused by a recreated test without a first-publish 409.
        await getDeployService().deleteABTest(projectId, deletingItem.id);
        await loadData();
      }
      setDeletingItem(null);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Delete failed';
      setError(message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleMoveToFolder = async (folderId: string | null) => {
    if (!projectId || !movingAbTest) return;
    const service = webPlacementServiceRef.current;
    await service.moveToFolder(projectId, movingAbTest.id, folderId);
    await loadData();
    setMovingAbTest(null);
  };

  // Get display URL based on custom domain and admin status
  const getDisplayUrl = useCallback((alias: string): { url: string | null; isDevUrl: boolean } => {
    if (activeCustomDomain) {
      return { url: getFunnelUrl(alias, activeCustomDomain), isDevUrl: false };
    }
    // Admins see dev URL for testing
    if (isAdmin) {
      return { url: getFunnelUrl(alias, null), isDevUrl: true };
    }
    return { url: null, isDevUrl: false };
  }, [activeCustomDomain, isAdmin]);

  // Deploy handler
  const handleDeploy = async (webPlacementId: string, abIteration?: WebABIterationPublishInput) => {
    // Block non-admins without custom domain
    if (!activeCustomDomain && !isAdmin) {
      return {
        success: false,
        webUrl: '',
        gcsFiles: [],
        error: 'Custom domain is required to publish. Please configure a domain first.',
      };
    }

    if (!projectId || !currentUser?.email) {
      return { success: false, webUrl: '', gcsFiles: [], error: 'Missing project or user data' };
    }
    return await deploy({ projectId, webPlacementId, userId: currentUser.email, abIteration });
  };

  // Create A/B Test handler for dialog onSave
  const handleCreateABTest = async (data: CreateWebPlacementInput | UpdateWebPlacementInput) => {
    if (!projectId || !currentUser?.email) throw new Error('Missing project or user data');
    const service = webPlacementServiceRef.current;

    const createData: CreateWebPlacementInput = {
      ...(data as CreateWebPlacementInput),
      type: 'ab_test',
      folderId: currentFolderId || null,
      createdBy: currentUser.email,
    };

    await service.create(projectId, createData);
    await loadData();
    setCreateDialogOpen(false);
  };

  // Update A/B Test handler for dialog onSave
  const handleUpdateABTest = async (data: CreateWebPlacementInput | UpdateWebPlacementInput) => {
    if (!projectId || !editingAbTest) throw new Error('Missing project or A/B test data');
    const service = webPlacementServiceRef.current;
    await service.update(projectId, editingAbTest.id, data as UpdateWebPlacementInput);
    await loadData();
    setEditingAbTest(null);
  };

  // Action menu handlers
  const handleEditClick = () => {
    if (menuItem && menuItem.itemType === 'abtest') {
      setEditingAbTest(menuItem);
    }
    handleMenuClose();
  };

  const handlePublishClick = () => {
    if (menuItem && menuItem.itemType === 'abtest') {
      setPublishingAbTest(menuItem);
    }
    handleMenuClose();
  };

  const handleMoveClick = () => {
    if (menuItem && menuItem.itemType === 'abtest') {
      setMovingAbTest(menuItem);
    }
    handleMenuClose();
  };

  const handleDeleteClick = async () => {
    if (!menuItem) return;

    // For folders, check contents count
    if (menuItem.itemType === 'folder') {
      const count = await getFolderContentsCount(menuItem.id);
      setFolderContentsCount(count);
    } else {
      setFolderContentsCount(0);
    }

    setDeletingItem(menuItem);
    handleMenuClose();
  };

  // Row click handler
  const handleRowClick = useCallback((item: WebABTestListItem) => {
    if (item.itemType === 'folder') {
      handleFolderClick(item);
    } else {
      // Open edit dialog for A/B tests
      setEditingAbTest(item);
    }
  }, [handleFolderClick]);

  // Guard: require auth and projectId
  if (!projectId || !currentUser) {
    return null;
  }

  const isLoading = loading || foldersLoading;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header — same title-row geometry as the Onboardings list (D-31):
          a 44px row, and the content (alert or table) starts ~37px under the
          title, where the Onboardings table starts below its bulk slot. This
          page has no row selection, so there is no bulk row. */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          minHeight: AB_TESTS_HEADER_ROW_MIN_HEIGHT_PX,
          mb: AB_TESTS_HEADER_ROW_MB,
        }}
      >
        <Typography variant="h5" component="h1" noWrap>
          Web A/B Tests
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
          {/* Secondary action icon-only, like the Onboardings header (D-13): one contained primary. */}
          <Tooltip title="Create folder">
            <IconButton
              color="primary"
              onClick={() => setCreateFolderDialogOpen(true)}
              aria-label="Create folder"
              data-testid="webabt-create-folder-btn"
              sx={{ width: AB_TESTS_HEADER_ROW_MIN_HEIGHT_PX, height: AB_TESTS_HEADER_ROW_MIN_HEIGHT_PX }}
            >
              <CreateNewFolderIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            disabled={!activeCustomDomain && !isAdmin}
            data-testid="webabt-create-btn"
          >
            Create A/B Test
          </Button>
        </Box>
      </Box>

      {/* Custom Domain Alert */}
      {!activeCustomDomain && !isLoading && !isAdmin && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>Custom Domain Required</AlertTitle>
          Web A/B Tests require a custom domain to function.
          Please configure a custom domain before creating A/B tests.
        </Alert>
      )}
      {!activeCustomDomain && !isLoading && isAdmin && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>No Custom Domain (Admin Mode)</AlertTitle>
          You can create A/B tests for testing, but they will only work after a custom domain is configured.
        </Alert>
      )}

      {/* Breadcrumb (when in folder) */}
      {currentFolderId && currentFolderName && (
        <Box sx={{ mb: 2 }}>
          <Breadcrumbs aria-label="breadcrumb" data-testid="webabt-breadcrumbs">
            <Link
              component="button"
              underline="hover"
              color="inherit"
              onClick={handleNavigateToRoot}
              sx={{ cursor: 'pointer' }}
              data-testid="webabt-breadcrumb-root"
            >
              Root
            </Link>
            <Typography color="text.primary">{currentFolderName}</Typography>
          </Breadcrumbs>
        </Box>
      )}

      {/* Loading state */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Error state */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Empty state */}
      {!isLoading && !error && items.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="text.secondary">
            {currentFolderId
              ? 'This folder is empty. Create an A/B test to get started.'
              : 'No A/B tests found. Create your first A/B test to get started.'}
          </Typography>
        </Paper>
      )}

      {/* A/B Tests table */}
      {!isLoading && !error && items.length > 0 && (
        <Paper>
          <Table data-testid="webabt-table">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell width={100}>Status</TableCell>
                <TableCell>Variants</TableCell>
                <TableCell>URL</TableCell>
                <TableCell width={120}>Updated</TableCell>
                <TableCell width={60} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {items.map((item) => {
                const isAbTest = item.itemType === 'abtest';
                const { url, isDevUrl } = isAbTest
                  ? getDisplayUrl(item.alias)
                  : { url: null, isDevUrl: false };

                return (
                  <TableRow
                    key={item.id}
                    hover
                    onClick={() => handleRowClick(item)}
                    sx={{ cursor: 'pointer' }}
                    data-testid={`webabt-row-${item.id}`}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {item.itemType === 'folder' ? (
                          <FolderIcon color="primary" />
                        ) : (
                          <ScienceIcon color="secondary" />
                        )}
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {item.name}
                          </Typography>
                          {isAbTest && item.description && (
                            <Typography variant="caption" color="text.secondary">
                              {item.description}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      {isAbTest ? (
                        <Chip
                          label={item.status}
                          size="small"
                          color={item.status === 'published' ? 'success' : 'default'}
                          variant={item.status === 'published' ? 'filled' : 'outlined'}
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {isAbTest ? (
                        <Typography variant="body2" color="text.secondary">
                          {formatVariants(item.variants)}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {isAbTest && item.status === 'published' && url ? (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Tooltip title={isDevUrl ? '⚠️ Dev URL - may not work without custom domain' : 'Open URL'}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.75rem',
                                maxWidth: 200,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                color: isDevUrl ? 'warning.main' : 'inherit',
                              }}
                            >
                              {url}
                            </Typography>
                          </Tooltip>
                          <Tooltip title={copiedUrl === url ? 'Copied!' : 'Copy URL'}>
                            <IconButton
                              size="small"
                              onClick={(e) => handleCopyUrl(url, e)}
                              data-testid={`webabt-copy-url-${item.id}`}
                            >
                              {copiedUrl === url ? (
                                <CheckIcon fontSize="small" color="success" />
                              ) : (
                                <CopyIcon fontSize="small" />
                              )}
                            </IconButton>
                          </Tooltip>
                        </Box>
                      ) : isAbTest && item.status === 'published' && !url ? (
                        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                          Requires custom domain
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {isAbTest ? 'Not published' : '—'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(item.updatedAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={(e) => handleMenuOpen(e, item)}
                        data-testid={`webabt-actions-${item.id}`}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        {menuItem?.itemType === 'abtest' && (
          <>
            <MenuItem onClick={handleEditClick} data-testid="webabt-menu-edit">
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
            <MenuItem onClick={handlePublishClick} data-testid="webabt-menu-publish">
              <ListItemIcon>
                <PublishIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Publish</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleMoveClick} data-testid="webabt-menu-move">
              <ListItemIcon>
                <MoveIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Move to folder</ListItemText>
            </MenuItem>
            <Divider />
          </>
        )}
        <MenuItem onClick={handleDeleteClick} sx={{ color: 'error.main' }} data-testid="webabt-menu-delete">
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Create A/B Test Dialog */}
      <WebABTestDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        projectId={projectId || ''}
        mode="create"
        onSave={handleCreateABTest}
      />

      {/* Create Folder Dialog */}
      <CreateWebABTestFolderDialog
        open={createFolderDialogOpen}
        onClose={() => setCreateFolderDialogOpen(false)}
        onCreate={handleCreateFolder}
      />

      {/* Edit Dialog */}
      {editingAbTest && (
        <WebABTestDialog
          open={true}
          onClose={() => setEditingAbTest(null)}
          projectId={projectId || ''}
          mode="edit"
          existingABTest={editingAbTest}
          onSave={handleUpdateABTest}
        />
      )}

      {/* Publish Dialog */}
      {publishingAbTest && (
        <DeployWebPlacementDialog
          open={true}
          onClose={() => {
            setPublishingAbTest(null);
            loadData();
          }}
          webPlacement={publishingAbTest}
          onDeploy={handleDeploy}
          projectId={projectId}
        />
      )}

      {/* Move to Folder Dialog */}
      {movingAbTest && (
        <MoveWebABTestToFolderDialog
          open={true}
          onClose={() => setMovingAbTest(null)}
          onMove={handleMoveToFolder}
          folders={folders}
          currentFolderId={movingAbTest.folderId}
          itemName={movingAbTest.name}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <SDialog
        open={Boolean(deletingItem)}
        onClose={() => setDeletingItem(null)}
        title={deletingItem?.itemType === 'folder' ? 'Delete Folder' : 'Delete A/B Test'}
        maxWidth="xs"
        fullWidth
        // Destructive confirmation — footer Cancel is the only dismiss.
        showCloseButton={false}
        primaryAction={{
          label: 'Delete',
          onClick: handleDelete,
          loading: deleteLoading,
          color: 'error',
          disabled: deletingItem?.itemType === 'folder' && folderContentsCount > 0,
        }}
        secondaryAction={{
          label: 'Cancel',
          onClick: () => setDeletingItem(null),
          disabled: deleteLoading,
        }}
      >
        {deletingItem?.itemType === 'folder' ? (
          folderContentsCount > 0 ? (
            <Typography>
              This folder contains <strong>{folderContentsCount}</strong> A/B test(s).
              Please move or delete the contents before deleting this folder.
            </Typography>
          ) : (
            <Typography>
              Are you sure you want to delete the folder <strong>{deletingItem?.name}</strong>?
              This action cannot be undone.
            </Typography>
          )
        ) : (
          <>
            <Typography>
              Are you sure you want to delete <strong>{deletingItem?.name}</strong>?
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              This action cannot be undone.
            </Typography>
          </>
        )}
      </SDialog>
    </Box>
  );
}

export default WebABTestsPage;
